<template>
  <BaseNavigationBar title="Редактирование шаблона" />

  <BaseIsland size="lg">
    <form class="one-template-edit" @submit.prevent="handleSave">
      <!-- Header: Название и описание проекта -->
      <div class="one-template-edit__header">
        <BaseForm
          ref="formRef1"
          v-model="formModel"
          :schema="OneHeaderSchema"
          :with-form-wrapper="false"
          :with-error-message="false"
        />
        <TemplateLandingButtonSlot
          :button="buttons.header"
          @add="handleStartAddButton('header')"
          @edit="handleOpenModuleSelect('header')"
          @clear="handleClearButton('header')"
        />
      </div>

      <TemplateMediaSlot
        :model-value="images[0]"
        class="one-template-edit__media-slot one-template-edit__media-slot--hero"
        @update:model-value="(urls) => handleUpdateImage(0, urls)"
      />

      <!-- Section: Заголовок и описание первой секции -->
      <div class="one-template-edit__section">
        <BaseForm
          ref="formRef2"
          v-model="formModel"
          :schema="OneSectionSchema"
          :with-form-wrapper="false"
          :with-error-message="false"
        />
        <TemplateLandingButtonSlot
          :button="buttons.section"
          @add="handleStartAddButton('section')"
          @edit="handleOpenModuleSelect('section')"
          @clear="handleClearButton('section')"
        />
      </div>

      <!-- Два блока с изображениями в строку -->
      <div class="one-template-edit__image-row">
        <div class="one-template-edit__image-block">
          <TemplateMediaSlot
            :model-value="images[1]"
            class="one-template-edit__media-slot one-template-edit__media-slot--section"
            @update:model-value="(urls) => handleUpdateImage(1, urls)"
          />
        </div>
        <div class="one-template-edit__image-block">
          <TemplateMediaSlot
            :model-value="images[2]"
            class="one-template-edit__media-slot one-template-edit__media-slot--section"
            @update:model-value="(urls) => handleUpdateImage(2, urls)"
          />
        </div>
      </div>

      <!-- Блоки контента друг напротив друга -->
      <div class="one-template-edit__unified-content">
        <!-- Левый блок -->
        <div class="one-template-edit__unified-block">
          <BaseForm
            ref="formRef3"
            v-model="formModel"
            :schema="OneLeftBlockSchema"
            :with-form-wrapper="false"
            :with-error-message="false"
          />
          <TemplateLandingButtonSlot
            full-width
            :button="buttons.leftBlock"
            @add="handleStartAddButton('leftBlock')"
            @edit="handleOpenModuleSelect('leftBlock')"
            @clear="handleClearButton('leftBlock')"
          />
          <TemplateMediaSlot
            :model-value="images[3]"
            class="one-template-edit__media-slot one-template-edit__media-slot--block"
            @update:model-value="(urls) => handleUpdateImage(3, urls)"
          />
        </div>

        <!-- Правый блок -->
        <div class="one-template-edit__unified-block">
          <TemplateMediaSlot
            :model-value="images[4]"
            class="one-template-edit__media-slot one-template-edit__media-slot--block"
            @update:model-value="(urls) => handleUpdateImage(4, urls)"
          />
          <BaseForm
            ref="formRef4"
            v-model="formModel"
            :schema="OneRightBlockSchema"
            :with-form-wrapper="false"
            :with-error-message="false"
          />
          <TemplateLandingButtonSlot
            full-width
            :button="buttons.rightBlock"
            @add="handleStartAddButton('rightBlock')"
            @edit="handleOpenModuleSelect('rightBlock')"
            @clear="handleClearButton('rightBlock')"
          />
        </div>
      </div>

      <BaseButton
        variant="primary"
        type="submit"
        max-width="335px"
        with-full
        with-decor
        :loading="loading.value"
        :disabled="loading.value"
      >
        Сохранить
      </BaseButton>
    </form>
  </BaseIsland>

  <LandingButtonTypePopup
    v-model="isButtonTypePopupOpen"
    @select-module="handleChooseButtonType('module')"
    @select-object="handleChooseButtonType('moduleObject')"
  />
</template>

<script setup lang="ts">
import type { IOneTemplateData, IOneTemplateFormData } from '~/entities/projects/model/templates-interface'
import type {
  IFormExpose,
} from '~/shared/ui'
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useTemplateEditProjectOrRequestMeta } from '~/entities/projects'
import { templateId } from '~/entities/projects/model/const'
import { navigateAfterTemplateSave } from '~/entities/projects/model/navigateAfterTemplateSave'
import { validateTemplateImages } from '~/entities/projects/model/templateImageValidation'
import {
  OneHeaderSchema,
  OneLeftBlockSchema,
  OneRightBlockSchema,
  OneSectionSchema,
} from '~/entities/projects/model/templates-schema'
import { uploadProjectImage } from '~/entities/projects/model/uploadProjectImage'
import { useTemplateEditButtons } from '~/entities/projects/model/useTemplateEditButtons'
import { useTemplateManager } from '~/entities/projects/model/useTemplateManager'
import LandingButtonTypePopup from '~/entities/projects/ui/templates/LandingButtonTypePopup.vue'
import TemplateLandingButtonSlot from '~/entities/projects/ui/templates/TemplateLandingButtonSlot.vue'
import TemplateMediaSlot from '~/entities/projects/ui/templates/TemplateMediaSlot.vue'
import { permissions, useLoading } from '~/shared/lib'
import {
  BaseButton,
  BaseForm,
  BaseIsland,
  BaseNavigationBar,
} from '~/shared/ui'

const router = useRouter()
const route = useRoute()
const { uploadFromBlobUrl } = uploadProjectImage()
const { isProjectRequestContext, resolvedProjectTitle, projectId, routeParamForLinks } = useTemplateEditProjectOrRequestMeta()
const { hasExistingTemplate, existingTemplate, saveTemplate } = useTemplateManager(() =>
  isProjectRequestContext.value
    ? { projectRequestId: route.params.id as string }
    : { projectId: projectId.value },
)

const formRef1 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef2 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef3 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef4 = ref<ComponentPublicInstance<IFormExpose>>()
const images = ref<string[]>([])
const loading = useLoading()

const formModel = ref<IOneTemplateFormData>({
  title: '',
  description: '',
  sectionTitle: '',
  sectionDescription: '',
  leftBlockTitle: '',
  leftBlockText: '',
  rightBlockTitle: '',
  rightBlockText: '',
})

const {
  buttons,
  isButtonTypePopupOpen,
  loadButtonsFromContent,
  handleStartAddButton,
  handleOpenModuleSelect,
  handleChooseButtonType,
  handleClearButton,
  getButtonsForSave,
  clearDraft,
} = useTemplateEditButtons({
  templateNumericId: templateId.one_template,
  editRoutePermission: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.ONE_TEMPLATE.EDIT.PERMISSION,
  getDraftPayload: () => ({
    formModel: { ...formModel.value },
    images: [...images.value],
    buttons: { ...buttons.value },
  }),
  applyDraftPayload: (draft) => {
    formModel.value = draft.formModel as IOneTemplateFormData
    images.value = [...draft.images]
    buttons.value = { ...draft.buttons }
  },
})

watch(existingTemplate, (template) => {
  if (template?.content && hasExistingTemplate.value) {
    const content = template.content as unknown as IOneTemplateData

    if (content.id === templateId.one_template) {
      formModel.value = {
        title: content.title || resolvedProjectTitle.value,
        description: content.description || '',
        sectionTitle: content.sectionTitle || '',
        sectionDescription: content.sectionDescription || '',
        leftBlockTitle: content.leftBlockTitle || '',
        leftBlockText: content.leftBlockText || '',
        rightBlockTitle: content.rightBlockTitle || '',
        rightBlockText: content.rightBlockText || '',
      }

      if (content.images) {
        images.value = [
          content.images.hero || '',
          content.images.sectionLeft || '',
          content.images.sectionRight || '',
          content.images.leftBlock || '',
          content.images.rightBlock || '',
        ]
      }

      loadButtonsFromContent(content.buttons)
    }
  }
}, { immediate: true })

watch(
  resolvedProjectTitle,
  (name) => {
    if (name && !formModel.value.title) {
      formModel.value.title = name
    }
  },
  { immediate: true },
)

function handleUpdateImage(index: number, urls: string[]) {
  const url = urls[0] ?? ''
  const next = [...images.value]
  while (next.length <= index) {
    next.push('')
  }
  next[index] = url
  images.value = next
}

async function handleSave() {
  const validationResults = await Promise.all([
    formRef1.value?.validate(),
    formRef2.value?.validate(),
    formRef3.value?.validate(),
    formRef4.value?.validate(),
  ])

  const allValid = validationResults.every(result => result?.valid)

  if (!allValid) {
    return
  }
  const uploadPromises = images.value.map(url => url ? uploadFromBlobUrl(url) : Promise.resolve(null))
  const uploadedUrls = await Promise.all(uploadPromises)
  const finalImages = images.value.map((image, index) =>
    uploadedUrls[index] || image,
  )
  if (!validateTemplateImages({
    requiredImageCount: 5,
    images: finalImages,
  })) {
    return
  }

  await loading.wrapLoading(async () => {
    const templateContent = {
      id: templateId.one_template,
      title: resolvedProjectTitle.value,
      description: formModel.value.description,
      sectionTitle: formModel.value.sectionTitle,
      sectionDescription: formModel.value.sectionDescription,
      leftBlockTitle: formModel.value.leftBlockTitle,
      leftBlockText: formModel.value.leftBlockText,
      rightBlockTitle: formModel.value.rightBlockTitle,
      rightBlockText: formModel.value.rightBlockText,
      buttons: getButtonsForSave(),
      images: {
        hero: finalImages[0],
        sectionLeft: finalImages[1],
        sectionRight: finalImages[2],
        leftBlock: finalImages[3],
        rightBlock: finalImages[4],
      },
    }

    await saveTemplate(templateContent as IOneTemplateData)
    clearDraft()

    await navigateAfterTemplateSave(
      router,
      route.query,
      isProjectRequestContext.value
        ? (route.params.id as string)
        : routeParamForLinks.value,
    )
  })
}
</script>

<style lang="scss" scoped>
.one-template-edit {
  display: grid;
  gap: rem(48px);
  grid-template-columns: 1fr;

  &__header {
    display: grid;
    gap: rem(16px);
  }

  &__section {
    display: grid;
    gap: rem(16px);
  }

  &__image-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__unified-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__unified-block {
    display: grid;
    gap: rem(16px);
  }

}
</style>
